<template>
    <component
        :is="url && !isEditActive ? 'router-link' : 'tr'"
        ref="row"
        :class="{
            'uik-table-row--container': container,
            'uik-table-row--focusable': focus,
            'uik-table-row--link': url,
            'uik-table-row--loading': getLoading,
            'uik-table-row--selected': isRowSelected,
            'uik-table-row--edit': isEditActive
        }"
        :to="url && !isEditActive ? url : false"
        class="uik-table-row"
        :tabindex="focus ? '0' : ''"
        @click="$emit('click')"
    >
        <UikTd
            v-if="getSelectedItems && !isEditActive"
            align="center"
            width="10"
        >
            <UikCheckbox
                tabindex="0"
                class="uik-table-row__select-checkbox"
                :value="isRowSelected"
                @click.native.prevent="toggleSelectedRow()"
            />
        </UikTd>
        <slot v-if="!isEditActive"/>
        <td
            v-if="$slots.edit && isEditActive"
            class="uik-table-edit"
            colspan="999"
            @keyup.enter="updateItem()"
        >
            <div class="uik-table-edit__container">
                <button
                    title="Close"
                    class="uik-table-edit__cancel"
                    @click="closeEdit()"
                >
                    <Uikon icon="close"/>
                </button>
                <main>
                    <slot name="edit"/>
                </main>
                <aside>
                    <UikButton
                        class="uik-table-edit__confirm"
                        success
                        icon="check"
                        text="Save"
                        :isLoading="getLoading"
                        @click="getLoading ? null : updateItem()"
                    />
                </aside>
            </div>
            <UikOverlay
                visible
                :opacity="2.5"
            />
        </td>
    </component>
</template>

<script>
import UikTd from "./Td"

export default {
    name: "UikTRow",
    components: { UikTd },
    props: {
        /**
         * Is component a container for rows
         */
        container: { type: Boolean, default: false },
        /**
         * URL to open on click
         */
        url: { type: String, default: "" },
        /**
         * Is row focuable - stays highlighted on click
         */
        focus: { type: Boolean, default: false },
        /**
         * Array of selected items
         */
        select: { type: Array, default: null },
        /**
         * Is row selected
         */
        selected: { type: Boolean, default: false },
        /**
         * Is component loading
         */
        isLoading: { type: [Boolean, null], default: null },
        /**
         * Is the edit row open/active
         */
        editActive: { type: [Boolean, null], default: null }
    },
    inject: ["resource", "selectedItems", "selectOptions"],
    data () {
        return {
            loading: false
        }
    },
    computed: {
        /**
         * The row's key prop (used when using row with v-for)
         */
        itemKey () {
            if (this.getSelectedItems) {
                if (this.$vnode.key) return this.$vnode.key
                if (this.$parent.$vnode.key) return this.$parent.$vnode.key
                if (this.$parent.$parent.$vnode.key) return this.$parent.$parent.$vnode.key
            }
            return ""
        },
        /**
         * Gets array of selected items from Table component or self
         */
        getSelectedItems () {
            return this.selectedItems || this.select
        },
        /**
         * Is the row currently selected?
         *
         * @return {Boolean}
         */
        isRowSelected () {
            if (this.selected || (this.getSelectedItems && this.getSelectedItems.includes(this.itemKey))) return true
            return false
        },
        /**
         * Is the edit mode (edit row) active/open
         *
         * @return  {Boolean}
         */
        isEditActive () {
            if (this.editActive) return true
            if (this.resource && this.resource.state.editId === this.$vnode.key) return true
            return false
        },
        /**
         * Gets the active loading state from prop or internal state
         *
         * @return {Boolean}
         */
        getLoading () {
            if (this.isLoading !== null) return this.isLoading
            return this.loading
        }
    },
    created () {
        if (this.getSelectedItems && !this.selectOptions.includes(this.itemKey)) this.selectOptions.push(this.itemKey)
    },
    beforeDestroy () {
        if (this.getSelectedItems && this.selectOptions.includes(this.itemKey)) {
            const index = this.selectOptions.indexOf(this.itemKey)
            this.selectOptions.splice(index, 1)
        }
    },
    methods: {
        /**
         * Toggles row's selected state
         */
        toggleSelectedRow () {
            if (this.isRowSelected) {
                let index = this.getSelectedItems.indexOf(this.itemKey)
                this.getSelectedItems.splice(index, 1)
                this.$emit("unselected", this.itemKey)
            } else {
                this.getSelectedItems.push(this.itemKey)
                this.$emit("selected", this.itemKey)
            }
            this.$emit("select", this.getSelectedItems)
        },
        /**
         * Emits the storeEditItem event and if a resource is injected stores the edit data
         */
        async updateItem () {
            this.$emit("storeEditItem")
            if (this.resource) {
                this.loading = true
                let res = await this.resource.storeEditItem()
                this.loading = false
                if (res.status === 200 || res.status === 201) this.resource.getList()
            }
        },
        /**
         * Emits edit close and if a resource is injected clears errors and edit state
         */
        closeEdit () {
            this.$emit("editClose")
            if (this.resource) {
                this.resource.clearEdit()
                this.resource.clearErrors()
            }
        }
    }
}
</script>

<style lang="scss">
.uik-table-row {
  text-decoration: none;
  display: table-row;
  vertical-align: middle;
  transition: all 0.15s;
  background: white;
  &:hover {
    background: darken($bg, 1%);
    transition: all 0.1s;
  }
  &--link {
    &:active {
      background: lighten($primary, 50%);
    }
  }
  &--focusable {
    &:focus {
      background: lighten($primary, 50%);
      &:hover {
        background: lighten($primary, 48.5%);
      }
      &:active {
        background: lighten($primary, 50%);
      }
    }
  }
  &--container {
    display: table-row-group;
  }
  &--loading {
    transition: all 0.5s;
    pointer-events: none;
    opacity: 0.5;
  }
  &--selected {
    box-shadow: inset 3px 0 0 $success;
    background: lighten($success, 54%);
    &:hover {
      background: lighten($success, 54%);
    }
  }
  &--edit {
      background: $bg;
      position: relative;
      &:hover {
          background: $bg;
      }
      .uik-table-edit {
        box-shadow: inset 0 1px 3px 0 rgba(0, 0, 0, 0.04), inset 0 -1px 3px 0 rgba(0, 0, 0, 0.04);
        cursor: initial;
        padding: 10px;
        animation: edit-mode-offset 0.15s ease-out;
        @keyframes edit-mode-offset {
            from {
                padding: 0;
            }
            to {
                padding: 10px;
            }
        }
        .uik-table-edit__container {
            background: white;
            border: $borderDefault;
            border-radius: 4px;
            box-shadow: $boxShadow;
            display: flex;
            flex-flow: row nowrap;
            justify-content: space-between;
            align-items: center;
            position: relative;
            z-index: 101;
            main {
                padding: 22px 20px 25px 25px;
                display: flex;
                flex-flow: row nowrap;
                justify-content: flex-start;
                align-items: center;
                max-width: 100%;
                width: 100%;
                > * {
                    flex-grow: 1;
                    min-width: 130px;
                    // max-width: 330px;
                    animation: edit-mode-content 0.2s;
                }
            }
            .uik-table-edit__cancel {
                position: absolute;
                top: 5px;
                right: 5px;
                display: flex;
                flex-flow: row nowrap;
                justify-content: center;
                align-items: center;
                background: transparent;
                outline: none;
                border: none;
                font-size: 14px;
                padding: 6px;
                color: $textSecondary;
                cursor: pointer;
                transition: all 0.15s;
                animation: edit-mode-content 0.15s;
                &::after {
                    position: absolute;
                    content: "";
                    width: 26px;
                    height: 26px;
                    border-radius: 50%;
                    background: rgba($borderColor, 0.8);
                    opacity: 0;
                    transform: scale(0.5);
                    transition: all 0.15s;
                }
                .uikon {
                    position: relative;
                    z-index: 1;
                }
                &:hover {
                    color: rgba($textMain, 0.8);
                    transform: scale(1.1);
                    &::after {
                        transform: none;
                        opacity: 1;
                    }
                }
                &:active {
                    transform: none;
                    &::after {
                        transform: scale(0.85);
                        opacity: 1;
                    }
                }
            }
            aside {
                transform: translateY(10px);
                padding-right: 25px;
                display: flex;
                flex-flow: row nowrap;
                justify-content: center;
                align-items: center;
                .uik-btn__base {
                    height: 37px;
                    padding: 0 15px;
                }
                .uik-table-edit__confirm {
                    .uik-btn__content {
                        .uikon {
                            opacity: 1;
                            font-size: 10px;
                        }
                    }
                }
                > * {
                    animation: edit-mode-content 0.15s;
                    & + * {
                        margin-left: 10px;
                    }
                }
            }
            @keyframes edit-mode-content {
                from {
                    opacity: 0;
                }
                to {
                    opacity: 1;
                }
            }
            .uik-input__wrapper {
            .uik-input__input {
                margin-bottom: 0;
                }
            }
            .uik-input__wrapper,
            .uik-select__wrapper {
                .uik-input__label-container {
                    margin-top: 0;
                    .uik-input__label {
                        padding-left: 2px;
                        text-transform: none;
                        color: rgba($textBase, 0.8);
                        font-size: 13px;
                        line-height: 15px;
                        font-weight: 500;
                    }
                }
                & + .uik-input__wrapper,
                & + .uik-select__wrapper {
                    margin-left: 17px;
                }
            }
            .uik-select__valueRendered {
                box-shadow: none;
                border-color: $borderColor;
                &:focus {
                    border-color: $primary;
                }
                &--error {
                    border-color: $error;
                }
                &--readonly {
                    cursor: default;
                    &:focus {
                        border-color: $borderColor;
                    }
                }
                .uik-btn__content {
                    max-width: 100%;
                    .uik-select__valueRenderedWrapper {
                        .uik-select__valueWrapper {
                            max-width: 100%;
                            overflow: hidden;
                            text-overflow: ellipsis;
                        }
                    }
                }
            }
            .uik-select__optionListWrapper {
                .uik-select__optionList {
                    max-height: 111px;
                }
            }
        }
        > .uik-overlay {
            z-index: 100;
            pointer-events: none;
        }
    }
    &:nth-child(2), &:last-child {
        .uik-table-edit {
            .uik-table-edit__container {
                .uik-select__optionListWrapper {
                    top: unset;
                    bottom: -2px;
                    left: calc(100% + 5px);
                }
            }
        }
    }
  }
  & + .uik-table-row {
    border-top: $borderDefault;
  }
  .uik-table-row__select-checkbox {
        transition: all 0.15s;
        &:hover {
            transform: scale(1.25);
        }
        &:focus {
            transform: none;
        }
    }
}
</style>
